/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { useI18n } from "hooks/i18n/i18n";
import { PRODUCT_TYPE } from "pages/AccountSettlementHistoryOrderId/constants/ProductType";
import {
  infoDetailTextStyle,
  infoOptionalTextStyle,
  infoTitleTextStyle,
  infoWrapperStyle,
} from "pages/AccountSettlementHistoryOrderId/styles/commonStyles";
import React from "react";

import {
  goodsCardStyle,
  liveImageContainerStyle,
  liveImageSkeletonStyle,
  liveImageStyle,
  rootStyle,
  thumbnailImageStyle,
  thumbnailSkeletonStyle,
  liveCardStyle,
  statusBarStyle,
  infoTextStyle,
  statusBadgeStyle,
} from "./styles";

import type { i18nStringType } from "i18n/i18nString";
import type { FC } from "react";

type ProductType = (typeof PRODUCT_TYPE)[keyof typeof PRODUCT_TYPE];

type LiveTicketInfo = {
  openDate: string;
  openTime: string;
  status: i18nStringType;
  eventId: string;
};

type Props = {
  type: ProductType;
  imageSrc: string;
  isImageLoading: boolean;
  productName: string;
  price: number;
  quantity: number;
  variationName: string;
  liveTicketInfo?: LiveTicketInfo;
};

type ProductDetailsProps = {
  productName: string;
  price: number;
  quantity: number;
  variationName: string;
  children?: React.ReactNode;
};

const StatusBar: FC<{ liveTicketInfo: LiveTicketInfo }> = ({
  liveTicketInfo,
}) => {
  const { t } = useI18n();
  return (
    <div css={statusBarStyle}>
      <p css={infoTextStyle}>
        <span>{liveTicketInfo.openDate}</span>
        {liveTicketInfo.openTime && <span>OPEN {liveTicketInfo.openTime}</span>}
      </p>
      <div css={statusBadgeStyle}>{t(liveTicketInfo.status)}</div>
    </div>
  );
};

const ProductDetails: FC<ProductDetailsProps> = ({
  productName,
  price,
  quantity,
  variationName,
  children,
}) => {
  const { t } = useI18n();
  return (
    <div css={infoWrapperStyle}>
      {children}
      <h4 css={infoTitleTextStyle}>{variationName}</h4>
      <p css={infoDetailTextStyle}>
        <span>¥{price.toLocaleString("ja-JP")}/</span>
        <span>
          {t("settlementHistory.productDetails.quantity", { quantity })}
        </span>
      </p>
      <p css={infoOptionalTextStyle}>{productName}</p>
    </div>
  );
};

export const ProductInfo: FC<Props> = ({
  type,
  imageSrc,
  isImageLoading,
  productName,
  price,
  quantity,
  variationName,
  liveTicketInfo,
}) => {
  const isLiveTicket = type === PRODUCT_TYPE.TICKET;

  return (
    <div css={rootStyle}>
      {isLiveTicket && (
        <div css={liveImageContainerStyle}>
          {isImageLoading ? (
            <div css={liveImageSkeletonStyle} />
          ) : (
            <img
              css={liveImageStyle}
              src={imageSrc}
              alt={productName}
              width="440"
              height="185"
            />
          )}
        </div>
      )}
      <div css={isLiveTicket ? liveCardStyle : goodsCardStyle}>
        {!isLiveTicket &&
          (isImageLoading ? (
            <div css={thumbnailSkeletonStyle} />
          ) : (
            <img
              css={thumbnailImageStyle}
              src={imageSrc}
              alt={productName}
              width="80"
              height="80"
            />
          ))}
        <ProductDetails
          productName={productName}
          price={price}
          quantity={quantity}
          variationName={variationName}
        >
          {liveTicketInfo?.openDate && (
            <StatusBar liveTicketInfo={liveTicketInfo} />
          )}
        </ProductDetails>
      </div>
    </div>
  );
};
